import React, { useState, useEffect, useMemo } from "react";
import { object, string, bool } from "prop-types";
import { loadScript, createPlayer } from "./youtubeService.js";

import uniqueid from "lodash.uniqueid";
import cx from "classnames";
import Icon from "../Icon/Icon.jsx";

const propTypes = {
  id: string.isRequired,
  poster: object,
  transcriptLink: object,
  caption: string,
  darkButton: bool,
  autoplay: string,
};

const YouTubeVideoApp = (props) => {
  const { id, poster, transcriptLink, caption, darkButton, autoplay } = props;

  const [videoStarted, setVideoStarted] = useState(false);
  const [player, setPlayer] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(autoplay);

  const playerId = useMemo(() => uniqueid(), []);

  useEffect(() => {
    // On mount, check to see if the API script is already loaded
    loadScript();

    // Check if window.onYouTubeIframeAPIReady already exists
    if (window.YT) {
      const newPlayer = createPlayer(playerId, id, onPlayerReady, autoplay);
      setPlayer(newPlayer);
    } else {
      // after onYouTubeIframeAPIReady is executed, create a new player
      const callback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (callback) {
          callback();
        }

        const newPlayer = createPlayer(playerId, id, onPlayerReady, autoplay);
        setPlayer(newPlayer);
      };
    }
  }, [id, playerId]);

  useEffect(() => {
    // If video's started but player is not ready, play video when ready
    if (videoStarted && playerReady) {
      player.playVideo();
    }
  }, [player, playerReady, videoStarted]);

  const onPlayerReady = () => {
    setPlayerReady(true);
  };

  const playVideo = () => {
    setVideoStarted(true);

    if (playerReady) {
      player.playVideo();
    }
  };

  const pauseVideo = () => {
    if (isVideoPlaying) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
    setIsVideoPlaying(!isVideoPlaying);
  };

  return (
    <div
      className={cx("youtube-video__app", {
        "youtube-video--video-started": videoStarted,
      })}
    >
      <figure className="youtube-video__media">
        <div className="youtube-video__video-wrapper">
          {autoplay !== "true" ? (
            <button
              className={cx("youtube-video__poster-button", {
                "youtube-video__poster-button--dark": darkButton,
              })}
              onClick={playVideo}
              type="button"
            >
              {poster && poster.small ? (
                <img
                  className="youtube-video__poster-image"
                  alt={poster.alt}
                  srcSet={`${poster.small} 480w, ${poster.medium} 680w, ${poster.large} 1292w`}
                />
              ) : (
                <img
                  className="youtube-video__poster-image"
                  alt=""
                  src={`https://i3.ytimg.com/vi/${id}/maxresdefault.jpg`}
                />
              )}
              <div className="background-video__icon-container yt-icons">
                  {!isVideoPlaying ?
                  <Icon
                    iconName="play"
                    className="background-video__play"
                    title="play video"
                  />
                :
                  <Icon
                    iconName="pause"
                    className="background-video__pause"
                    title="pause video"
                  />
                }
              </div>
            </button>
          ) : (
            <button
              className={cx("youtube-video__button", {
                "youtube-video__poster-button--dark": darkButton,
                "youtube-video__button--play":
                  autoplay === "true" && !isVideoPlaying,
                "youtube-video__button--paused":
                  autoplay === "true" && isVideoPlaying,
              })}
              onClick={pauseVideo}
              type="button"
              aria-label={isVideoPlaying ? "Pause video" : "Play video"}
            >
              <div className="background-video__icon-container yt-icons">
                {!isVideoPlaying ?
                  <Icon
                    iconName="play"
                    className="background-video__play"
                    title="play video"
                  />
                :
                  <Icon
                    iconName="pause"
                    className="background-video__pause"
                    title="pause video"
                  />
                }
              </div>
            </button>
          )}
          <div id={playerId} />
        </div>
        <div className="youtube-video__caption">
          {caption && (
            <figcaption className="youtube-video__figure-caption">
              {caption && (
                <span className="youtube-video__description">{caption}</span>
              )}
            </figcaption>
          )}
          {transcriptLink && transcriptLink.href && (
            <div className="youtube-video__transcript">
              <a className="link" href={transcriptLink.href}>
                {transcriptLink.text}
              </a>
            </div>
          )}
        </div>
      </figure>
    </div>
  );
};

YouTubeVideoApp.propTypes = propTypes;
export default YouTubeVideoApp;
